/**
 * WebRTC DataChannel / control-plane wire protocol (agent ↔ viewer).
 * Desktop pixels and keystrokes travel on the data plane only — never Firestore.
 */

import type { EncodingPreference } from "./sessions";

/** DataChannel labels used by the agent/viewer. */
export const DATA_CHANNEL = {
  /** Reliable: session auth, quality, cursor, input ack, mode switches. */
  control: "srd-control",
  /** Partially-reliable preferred: UI-tile frames + move rects. */
  tiles: "srd-tiles",
  /** File transfer v1 (reliable, size-limited). */
  file: "srd-file",
} as const;

/** Quality / performance operating modes. */
export type QualityMode = "auto" | "ui_sharp" | "ui_save_bandwidth" | "video_smooth";

/** Active encode path after adaptation. */
export type ActiveEncodeMode = "ui_tile" | "h264_hw" | "h264_sw";

export interface QualityLadderStep {
  mode: ActiveEncodeMode;
  maxWidth: number;
  maxFps: number;
  /** JPEG/WebP quality 1–100 for ui_tile; ignored for H.264. */
  tileQuality: number;
  /** Target bitrate kbps for H.264 path. */
  videoBitrateKbps: number;
}

/** Default auto-adaptation ladder (prefer sharp UI on slow links). */
export const DEFAULT_QUALITY_LADDER: readonly QualityLadderStep[] = [
  { mode: "ui_tile", maxWidth: 1920, maxFps: 15, tileQuality: 80, videoBitrateKbps: 0 },
  { mode: "ui_tile", maxWidth: 1600, maxFps: 12, tileQuality: 72, videoBitrateKbps: 0 },
  { mode: "ui_tile", maxWidth: 1280, maxFps: 10, tileQuality: 65, videoBitrateKbps: 0 },
  { mode: "ui_tile", maxWidth: 1024, maxFps: 8, tileQuality: 55, videoBitrateKbps: 0 },
  { mode: "h264_hw", maxWidth: 1280, maxFps: 20, tileQuality: 0, videoBitrateKbps: 2500 },
  { mode: "h264_hw", maxWidth: 960, maxFps: 15, tileQuality: 0, videoBitrateKbps: 1200 },
  /** Software H.264 is non-default and feature-flagged — last resort only. */
  { mode: "h264_sw", maxWidth: 960, maxFps: 8, tileQuality: 0, videoBitrateKbps: 800 },
] as const;

export const TILE_DEFAULTS = {
  tileSize: 64,
  maxTilesPerFrame: 256,
  codec: "jpeg" as const,
} as const;

export const FILE_TRANSFER_LIMITS = {
  maxFileBytes: 100 * 1024 * 1024,
  maxChunkBytes: 64 * 1024,
} as const;

/** Binary tile frame header (little-endian) after JSON envelope on tiles channel. */
export interface TileRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MoveRect {
  srcX: number;
  srcY: number;
  dstX: number;
  dstY: number;
  w: number;
  h: number;
}

export type TileImageCodec = "jpeg" | "webp" | "png";

export interface TileFrameMessage {
  v: 1;
  type: "tile_frame";
  frameId: number;
  /** Capture timestamp (agent monotonic ms). */
  ts: number;
  screenW: number;
  screenH: number;
  codec: TileImageCodec;
  /** Dirty tiles; each payload is length-prefixed on the binary channel. */
  tiles: TileRect[];
  /** Optional copy/scroll blit hints (viewer applies before painting tiles). */
  moves?: MoveRect[];
}

export interface CursorOverlayMessage {
  v: 1;
  type: "cursor";
  x: number;
  y: number;
  visible: boolean;
  /** Optional cursor shape id; hotspot relative to shape. */
  shapeId?: string;
  hotX?: number;
  hotY?: number;
}

export type ControlMessage =
  | SessionAuthMessage
  | SessionAuthResultMessage
  | QualityUpdateMessage
  | StatsReportMessage
  | InputMouseMessage
  | InputKeyboardMessage
  | InputWheelMessage
  | ModeSwitchMessage
  | PingMessage
  | PongMessage
  | CursorOverlayMessage;

export interface SessionAuthMessage {
  v: 1;
  type: "session_auth";
  sessionId: string;
  /** Short-lived session JWT minted by mintSession (technician). */
  sessionJwt: string;
}

export interface SessionAuthResultMessage {
  v: 1;
  type: "session_auth_result";
  ok: boolean;
  reason?: string;
  encodeMode?: ActiveEncodeMode;
  encodingPreference?: EncodingPreference;
}

export interface QualityUpdateMessage {
  v: 1;
  type: "quality_update";
  mode: QualityMode;
  ladderIndex?: number;
  encodeMode?: ActiveEncodeMode;
  maxWidth?: number;
  maxFps?: number;
}

export interface StatsReportMessage {
  v: 1;
  type: "stats";
  rttMs?: number;
  throughputKbps?: number;
  encodeCpuPct?: number;
  queueDepth?: number;
  encodeMode: ActiveEncodeMode;
  fps: number;
}

export interface InputMouseMessage {
  v: 1;
  type: "mouse";
  action: "move" | "down" | "up" | "dbl";
  x: number;
  y: number;
  button?: "left" | "right" | "middle";
}

export interface InputKeyboardMessage {
  v: 1;
  type: "keyboard";
  action: "down" | "up";
  /** Windows VK code or browser key code — agent maps. */
  keyCode: number;
  text?: string;
  modifiers?: {
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
  };
}

export interface InputWheelMessage {
  v: 1;
  type: "wheel";
  x: number;
  y: number;
  deltaX: number;
  deltaY: number;
}

export interface ModeSwitchMessage {
  v: 1;
  type: "mode_switch";
  /** Requested preference; agent may refuse h264_sw unless feature enabled. */
  preference: EncodingPreference | "ui_tile" | "h264_hw";
}

export interface PingMessage {
  v: 1;
  type: "ping";
  nonce: number;
  t: number;
}

export interface PongMessage {
  v: 1;
  type: "pong";
  nonce: number;
  t: number;
}

export interface FileTransferHello {
  v: 1;
  type: "file_hello";
  transferId: string;
  fileName: string;
  sizeBytes: number;
  sha256?: string;
}

export interface FileTransferChunkMeta {
  v: 1;
  type: "file_chunk";
  transferId: string;
  offset: number;
  length: number;
}

export interface FileTransferDone {
  v: 1;
  type: "file_done";
  transferId: string;
  ok: boolean;
  reason?: string;
}

/** Firestore sessions/{id}/signaling/{messageId} (Admin SDK via postSessionSignal). */
export type SignalType = "offer" | "answer" | "ice" | "hangup";

export interface SessionSignalDocument {
  messageId: string;
  sessionId: string;
  type: SignalType;
  fromRole: "technician" | "device";
  fromUid: string;
  /** SDP text, or JSON-stringified RTCIceCandidateInit for `ice`. */
  payload: string;
  createdAt: string;
  participantUids: string[];
}

/**
 * Binary framing on `srd-tiles` (one DataChannel message per dirty frame):
 *   u32le jsonLen | utf8 TileFrameMessage JSON | repeated (u32le tileLen | tileBytes)
 * Tile byte order matches `TileFrameMessage.tiles`. Apply `moves` before painting tiles.
 */
export const TILE_FRAME_BINARY = {
  version: 1,
} as const;
